/**
 * Hōjin — Composants partagés Phase D
 *
 * NanaeHeader · NanaeFooter · SaisineCard · CadreThumbnail · QMetric · QSectionHeader
 *
 * Tous strictement sur tokens --qv3-* (noir/blanc/or/gris + LED rouge).
 * Aucun emoji, aucun icône colorée.
 */
import * as React from "react";
import { Link } from "@tanstack/react-router";
import { NanaeLogoFull, NanaeLogoSymbol } from "./NanaeLogo";
import { useAuth } from "@/contexts/AuthContext";
import { useClickOutside } from "@/hooks/useClickOutside";
import { PersonaProvider } from "@/components/qorum/PersonaProvider";
import pDelphine from "@/assets/landing-v3/p-delphine.jpg";
import pHenri from "@/assets/landing-v3/p-henri.jpg";
import pAkilah from "@/assets/landing-v3/p-akilah.jpg";
import pEva from "@/assets/landing-v3/p-eva.jpg";
import pRen from "@/assets/landing-v3/p-ren.jpg";
import pJulien from "@/assets/landing-v3/p-julien.jpg";
import pImani from "@/assets/landing-v3/p-imani.jpg";
import pPazuru from "@/assets/landing-v3/p-pazuru.jpg";

function cx(...c: Array<string | false | undefined>) {
  return c.filter(Boolean).join(" ");
}

/* =========================================================
 * <NanaeHeader />
 * Header unifié pages app. Logo · titre central · avatar.
 * ========================================================= */
export interface NanaeHeaderUser {
  email?: string | null;
  name?: string | null;
}

export function NanaeHeader({
  title,
  user,
  variant = "app",
  rightSlot,
}: {
  title?: string;
  user?: NanaeHeaderUser | null;
  variant?: "app" | "public" | "minimal";
  rightSlot?: React.ReactNode;
}) {
  const initials = React.useMemo(() => {
    const src = user?.name || user?.email || "";
    if (!src) return "·";
    const parts = src.trim().split(/[\s@.]+/).filter(Boolean);
    const a = parts[0]?.[0] ?? "";
    const b = parts[1]?.[0] ?? "";
    return (a + b).toUpperCase() || "·";
  }, [user]);

  return (
    <header className={cx("nanae-header", `is-${variant}`)} role="banner">
      <div className="nanae-header-inner">
        <Link to="/foyer" className="nanae-header-brand" aria-label="Hōjin · Le Foyer">
          <NanaeLogoFull
            layout="horizontal"
            symbolSize="md"
            wordmarkSize="md"
            withKanji
          />
        </Link>

        {title && (
          <div className="nanae-header-title">
            <h1>{title}</h1>
          </div>
        )}

        <div className="nanae-header-right">
          {rightSlot}
          <a
            href="https://conkete.com/cabinet"
            className="qv3-btn-link nanae-conkete-bridge"
            aria-label="Ouvrir mon espace Conkete"
          >
            Conkete
          </a>
          {variant !== "minimal" && user && (
            <NanaeAvatarMenu initials={initials} userLabel={user.name || user.email || "Compte"} />
          )}
          {variant === "minimal" && (
            <Link to="/foyer" className="qv3-btn-link nanae-header-back">
              Retour
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

/* =========================================================
 * <NanaeAvatarMenu />
 * Avatar cliquable → menu déroulant (Mon Compte · Le Greffe · Déconnexion).
 * ========================================================= */
function NanaeAvatarMenu({ initials, userLabel }: { initials: string; userLabel: string }) {
  const { signOut } = useAuth();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        className="nanae-header-avatar"
        aria-label={userLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer", font: "inherit", color: "inherit" }}
      >
        {initials}
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            minWidth: 200,
            background: "var(--ivory, #f3efe6)",
            color: "var(--charcoal, #1a1a1a)",
            border: "0.5px solid var(--stone-mid, #cfcabd)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            padding: "6px 0",
            zIndex: 50,
          }}
        >
          <Link
            to="/compte"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="qv3-mono-hud size-xs is-caps"
            style={{ display: "block", padding: "10px 16px", color: "inherit", textDecoration: "none" }}
          >
            Mon Compte
          </Link>
          <Link
            to="/archives"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="qv3-mono-hud size-xs is-caps"
            style={{ display: "block", padding: "10px 16px", color: "inherit", textDecoration: "none" }}
          >
            Le Greffe
          </Link>
          <div style={{ height: "0.5px", background: "var(--stone-mid, #cfcabd)", margin: "4px 0" }} />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
            className="qv3-mono-hud size-xs is-caps"
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              padding: "10px 16px",
              background: "transparent",
              border: 0,
              color: "inherit",
              cursor: "pointer",
              font: "inherit",
            }}
          >
            Déconnexion
          </button>
        </div>
      )}
    </div>
  );
}


/* =========================================================
 * <NanaeFooter />
 * ========================================================= */
export function NanaeFooter({
  variant = "minimal",
}: {
  variant?: "minimal" | "full";
}) {
  return (
    <footer className={cx("nanae-footer", `is-${variant}`)} role="contentinfo">
      <div className="nanae-footer-inner">
        <span className="qv3-mono-hud size-xs is-caps">
          SEPT VOIX, UNE DIRECTION
        </span>
        {variant === "full" && (
          <nav className="nanae-footer-nav" aria-label="Pied de page">
            <Link to="/legal" className="qv3-btn-link">Mentions légales</Link>
            <Link to="/mentions-legales" className="qv3-btn-link">CGU</Link>
            <Link to="/confidentialite" className="qv3-btn-link">Confidentialité</Link>
            <Link to="/cgv" className="qv3-btn-link">CGV</Link>
            <Link to="/politique-cookies" className="qv3-btn-link">Politique cookies</Link>
            <a href="https://conkete.com" className="qv3-btn-link">Conkete</a>
            <button
              type="button"
              className="qv3-btn-link"
              onClick={() => {
                void import("@/lib/cookie-consent").then((m) => m.openCookiePreferences());
              }}
            >
              Gérer les cookies
            </button>
          </nav>
        )}
        <span className="qv3-mono-hud size-xs is-caps">Hōjin · MMXXVI</span>
      </div>
    </footer>
  );
}

/* =========================================================
 * <QMetric label value />
 * Métrique style sérigraphie Sony — caps + mono.
 * ========================================================= */
export function QMetric({
  label,
  value,
  align = "left",
  size = "md",
}: {
  label: string;
  value: React.ReactNode;
  align?: "left" | "center";
  size?: "sm" | "md" | "lg";
}) {
  return (
    <div className={cx("qv3-metric", `align-${align}`, `size-${size}`)}>
      <span className="qv3-metric-label qv3-mono-hud size-xs is-caps">{label}</span>
      <span className="qv3-metric-value">{value}</span>
    </div>
  );
}

/* =========================================================
 * <QSectionHeader number title />
 * Eyebrow caps + titre uniformes.
 * ========================================================= */
export function QSectionHeader({
  number,
  title,
  hint,
}: {
  number?: string;
  title: string;
  hint?: string;
}) {
  return (
    <div className="qv3-section-header">
      <p className="qv3-mono-hud size-sm is-caps">
        {number ? `§ ${number} · ${title}` : title}
      </p>
      {hint && <p className="qv3-section-header-hint">{hint}</p>}
    </div>
  );
}

/* =========================================================
 * <CadreThumbnail />
 * Portrait N&B compact réutilisable.
 * Pas d'asset photo dispo en Phase D → initiale serif sur cercle or fin.
 * ========================================================= */
export type CadreData = {
  id: string;
  name: string;
  role?: string;
  imageUrl?: string | null;
};

export function CadreThumbnail({
  cadre,
  size = "md",
  showName = false,
}: {
  cadre: CadreData;
  size?: "sm" | "md" | "lg";
  showName?: boolean;
}) {
  const initial = cadre.name?.[0]?.toUpperCase() ?? "·";
  return (
    <figure className={cx("cadre-thumb", `size-${size}`)} title={cadre.name}>
      <div className="cadre-thumb-portrait" aria-hidden="true">
        {cadre.imageUrl ? (
          <img src={cadre.imageUrl} alt="" loading="lazy" />
        ) : (
          <span className="cadre-thumb-initial">{initial}</span>
        )}
      </div>
      {showName && (
        <figcaption className="cadre-thumb-name">
          <span className="cadre-thumb-name-first">{cadre.name}</span>
          {cadre.role && (
            <span className="qv3-mono-hud size-xs is-caps">{cadre.role}</span>
          )}
          <PersonaProvider personaId={cadre.id} logoSize={12} fontSize={10} />
        </figcaption>
      )}
    </figure>
  );
}

/* Casting Hōjin — 7 sages (placeholder pour Phase D, sans photos) */
export const NANAE_CADRES: CadreData[] = [
  { id: "delphine", name: "Delphine", role: "Stratège",      imageUrl: pDelphine },
  { id: "henri",    name: "Henri",    role: "Finance",       imageUrl: pHenri },
  { id: "akilah",   name: "Akilah",   role: "Analyste de marché", imageUrl: pAkilah },
  { id: "eva",      name: "Eva",      role: "Communication", imageUrl: pEva },
  { id: "ren",      name: "Ren",      role: "Analyse",       imageUrl: pRen },
  { id: "julien",   name: "Julien",   role: "Opérations",    imageUrl: pJulien },
  { id: "imani",    name: "Imani",    role: "Conscience",    imageUrl: pImani },
  { id: "pazuru",   name: "Fujiwara", role: "L'Invité PDF", imageUrl: pPazuru },
];

/* =========================================================
 * <SaisineCard />
 * Carte saisine pour listes (Foyer, Archives).
 * ========================================================= */
export type SaisineStatus = "active" | "maieutique" | "completed" | "failed" | "archived";

export type SaisineCardData = {
  id: string;
  title: string;
  date?: string | null;
  status: SaisineStatus;
  mode?: string | null;
};

const STATUS_LABEL: Record<SaisineStatus, string> = {
  active: "EN DÉBAT",
  maieutique: "ANTICHAMBRE",
  completed: "VERDICT RENDU",
  failed: "INTERROMPUE",
  archived: "ARCHIVÉE",
};

const STATUS_HINT: Record<SaisineStatus, string> = {
  completed: "Délibération terminée",
  active: "Le Conseil siège",
  maieutique: "Antichambre démarrée — convoquer le Conseil",
  failed: "Pipeline interrompu — vous pouvez relancer",
  archived: "Saisine archivée",
};

const STATUS_COLOR: Record<SaisineStatus, { dot: string; pulse: boolean }> = {
  completed: { dot: "#39FF14", pulse: false },
  active: { dot: "#3B82F6", pulse: true },
  maieutique: { dot: "#00E5FF", pulse: false },
  failed: { dot: "#A33445", pulse: false },
  archived: { dot: "#9A9A9A", pulse: false },
};

function formatDateFR(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

export function SaisineCard({
  saisine,
  action = "auto",
}: {
  saisine: SaisineCardData;
  action?: "auto" | "reprendre" | "voir-memo" | "voir-verdict";
}) {
  const resolvedAction =
    action === "auto"
      ? saisine.status === "completed"
        ? "voir-verdict"
        : "reprendre"
      : action;

  const href =
    resolvedAction === "voir-verdict"
      ? `/verdict/${saisine.id}`
      : resolvedAction === "voir-memo"
        ? `/memo/${saisine.id}`
        : saisine.status === "maieutique"
          ? `/antichambre?question=${encodeURIComponent(saisine.title)}`
          : `/deliberation/${saisine.id}`;

  const cta =
    resolvedAction === "voir-verdict"
      ? "Voir le verdict"
      : resolvedAction === "voir-memo"
        ? "Voir le mémo"
        : "Reprendre";

  const statusColor = STATUS_COLOR[saisine.status];
  const statusHint = STATUS_HINT[saisine.status];

  return (
    <article className="saisine-card" data-status={saisine.status}>
      <style>{`@keyframes saisine-dot-pulse { 0%,100%{opacity:1} 50%{opacity:.3} }`}</style>
      <div className="saisine-card-main">
        <div className="saisine-card-meta">
          <span
            aria-hidden="true"
            style={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: statusColor.dot,
              marginRight: 8,
              animation: statusColor.pulse ? "saisine-dot-pulse 1.4s ease-in-out infinite" : "none",
            }}
          />
          <span className="qv3-mono-hud size-xs is-caps">
            {formatDateFR(saisine.date)}
          </span>
          <span className="saisine-card-dot" aria-hidden="true">·</span>
          <span className="qv3-mono-hud size-xs is-caps saisine-card-status">
            {STATUS_LABEL[saisine.status]}
          </span>
          {saisine.mode && (
            <>
              <span className="saisine-card-dot" aria-hidden="true">·</span>
              <span className="qv3-mono-hud size-xs is-caps">{saisine.mode}</span>
            </>
          )}
        </div>
        <h3 className="saisine-card-title">{saisine.title}</h3>
        <p
          className="saisine-card-hint"
          style={{
            margin: "6px 0 0",
            fontFamily: '"Cormorant Garamond", serif',
            fontStyle: "italic",
            fontSize: 14,
            color: "var(--smoke, #6b6b6b)",
          }}
        >
          {statusHint}
        </p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
        <a className="qv3-btn-link saisine-card-cta" href={href}>
          {cta} →
        </a>
        {saisine.status === "completed" && resolvedAction === "voir-verdict" && (
          <>
            {(saisine.mode ?? "").toLowerCase() !== "verif" && (
              <a
                className="qv3-btn-link"
                href={`/deliberation/${saisine.id}`}
                style={{ fontSize: 12, opacity: 0.75 }}
              >
                Voir le délibéré
              </a>
            )}
            <a
              className="qv3-btn-link"
              href={`/memo/${saisine.id}`}
              style={{ fontSize: 12, opacity: 0.75 }}
            >
              Voir le mémo
            </a>
          </>
        )}
      </div>
    </article>
  );
}
